import {
  createContext,
  Dispatch,
  isValidElement,
  ReactNode,
  SetStateAction,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { equals } from "ramda";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

type HeaderVariant = "default" | "colored";

interface INextGenHeaderContext {
  breadcrumbs: BreadcrumbItem[];
  title: ReactNode;
  actions: [ReactNode?, ReactNode?];
  rightPane: ReactNode;
  variant: HeaderVariant;
  heroContent: ReactNode;
  // Renders a back-arrow button beside the title when set. Undefined hides it.
  onBack?: () => void;
  setBreadcrumbs: Dispatch<SetStateAction<BreadcrumbItem[]>>;
  setTitle: Dispatch<SetStateAction<ReactNode>>;
  setActions: Dispatch<SetStateAction<[ReactNode?, ReactNode?]>>;
  setRightPane: Dispatch<SetStateAction<ReactNode>>;
  setVariant: Dispatch<SetStateAction<HeaderVariant>>;
  setHeroContent: Dispatch<SetStateAction<ReactNode>>;
  setOnBack: Dispatch<SetStateAction<(() => void) | undefined>>;
}

const compareReactNodes = (node1: ReactNode, node2: ReactNode): boolean => {
  if (isValidElement(node1) && isValidElement(node2)) {
    return node1.type === node2.type && equals(node1.props, node2.props);
  }
  return equals(node1, node2);
};

const NextGenHeaderContext = createContext<INextGenHeaderContext>({
  breadcrumbs: [],
  title: null,
  actions: [],
  rightPane: null,
  variant: "default",
  heroContent: null,
  onBack: undefined,
  setBreadcrumbs: () => {},
  setTitle: () => {},
  setActions: () => {},
  setRightPane: () => {},
  setVariant: () => {},
  setHeroContent: () => {},
  setOnBack: () => {},
});

export const NextGenHeaderProvider = ({ children }: { children: ReactNode }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [title, setTitle] = useState<ReactNode>(null);
  const [actions, setActions] = useState<[ReactNode?, ReactNode?]>([]);
  const [rightPane, setRightPane] = useState<ReactNode>(null);
  const [variant, setVariant] = useState<HeaderVariant>("default");
  const [heroContent, setHeroContent] = useState<ReactNode>(null);
  const [onBack, setOnBack] = useState<(() => void) | undefined>(undefined);

  return (
    <NextGenHeaderContext.Provider
      value={{
        breadcrumbs,
        setBreadcrumbs,
        title,
        setTitle,
        actions,
        setActions,
        rightPane,
        setRightPane,
        variant,
        setVariant,
        heroContent,
        setHeroContent,
        onBack,
        setOnBack,
      }}
    >
      {children}
    </NextGenHeaderContext.Provider>
  );
};

const useNextGenHeader = ({
  breadcrumbs: initialBreadcrumbs,
  title: initialTitle,
  actions: initialActions,
  rightPane: initialRightPane,
  variant: initialVariant,
  heroContent: initialHeroContent,
  onBack: initialOnBack,
}: Partial<
  Pick<
    INextGenHeaderContext,
    "breadcrumbs" | "title" | "actions" | "rightPane" | "variant" | "heroContent" | "onBack"
  >
> = {}) => {
  const context = useContext(NextGenHeaderContext);

  if (!context) {
    throw new Error("useNextGenHeader must be used within a NextGenHeaderProvider");
  }

  const {
    setBreadcrumbs,
    setTitle,
    setActions,
    setRightPane,
    setVariant,
    setHeroContent,
    setOnBack,
  } = context;

  useLayoutEffect(() => {
    if (initialBreadcrumbs !== undefined && !equals(initialBreadcrumbs, context.breadcrumbs)) {
      setBreadcrumbs(initialBreadcrumbs);
    }
  }, [initialBreadcrumbs, context.breadcrumbs, setBreadcrumbs]);

  useLayoutEffect(() => {
    if (initialTitle !== undefined && !compareReactNodes(initialTitle, context.title)) {
      setTitle(initialTitle);
    }
  }, [initialTitle, context.title, setTitle]);

  useLayoutEffect(() => {
    if (initialActions !== undefined && !equals(initialActions, context.actions)) {
      setActions(initialActions);
    }
  }, [initialActions, context.actions, setActions]);

  useLayoutEffect(() => {
    if (initialRightPane !== undefined && !compareReactNodes(initialRightPane, context.rightPane)) {
      setRightPane(initialRightPane);
    }
  }, [initialRightPane, context.rightPane, setRightPane]);

  useLayoutEffect(() => {
    if (initialVariant !== undefined && initialVariant !== context.variant) {
      setVariant(initialVariant);
    }
  }, [initialVariant, context.variant, setVariant]);

  useLayoutEffect(() => {
    if (
      initialHeroContent !== undefined &&
      !compareReactNodes(initialHeroContent, context.heroContent)
    ) {
      setHeroContent(initialHeroContent);
    }
  }, [initialHeroContent, context.heroContent, setHeroContent]);

  // Callers commonly pass an inline closure (e.g. `() => navigate(-1)`), which is a
  // new reference every render. Comparing it by reference like the other fields
  // would set state - and re-render - every render, looping forever. Instead, only
  // react to whether a handler is present at all, and always call through to
  // whatever the latest one is via a ref.
  const onBackRef = useRef(initialOnBack);
  onBackRef.current = initialOnBack;
  const hasOnBack = initialOnBack !== undefined;

  // `useNextGenHeader()` is also called as a pure reader with no `onBack` (e.g.
  // by ManifoldNextGenAppHeader itself) — those instances must never clear a
  // back handler some other page just set. Only clear it when *this same
  // instance* previously set one and has now stopped wanting it.
  const [didSetOnBack, setDidSetOnBack] = useState(false);

  useLayoutEffect(() => {
    if (hasOnBack) {
      setOnBack(() => () => onBackRef.current?.());
      setDidSetOnBack(true);
    } else if (didSetOnBack) {
      setOnBack(undefined);
      setDidSetOnBack(false);
    }
  }, [hasOnBack, didSetOnBack, setOnBack]);

  // Reset all header state when the page unmounts so stale values don't bleed
  // into pages that don't call useNextGenHeader.
  useLayoutEffect(() => {
    return () => {
      setBreadcrumbs([]);
      setTitle(null);
      setActions([]);
      setRightPane(null);
      setVariant("default");
      setHeroContent(null);
      setOnBack(undefined);
    };
  }, [setBreadcrumbs, setTitle, setActions, setRightPane, setVariant, setHeroContent, setOnBack]);

  return context;
};

export type { BreadcrumbItem, HeaderVariant };
export default useNextGenHeader;
